import create from "zustand";
import { TransactionState } from "../common/enums/common.enums";
import { IDetailNftData } from "../common/interfaces/common.interface";
import { toggleModalOpenClass } from "../utilities/helpers";

export interface ITransactionInfo {
  txid: string | null;
  number: number;
  transactionState: TransactionState;
  description: string;
}

interface ITransactionStore {
  isProcessing: boolean;
  transactions: ITransactionInfo[];
  addTransactions: (transaction: ITransactionInfo[]) => void;
  processedTransactions: ITransactionInfo[];
  retryCallback: (() => Promise<void>) | null;
  updateCurrentTransaction: (
    transaction: ITransactionInfo,
    retryCallback?: () => Promise<void>
  ) => void;
  closeTransactionProcess: () => void;
  startProcessing: (transactions: ITransactionInfo[]) => void;
  updateProcessedTransactions: (transaction: ITransactionInfo) => void;
  warningDescription?: string;
  customTitle?: string;
  nftData?: IDetailNftData;
  setWarningDescription: (description: string) => void;
  setCustomTitle: (title: string) => void;
  setNftData: (data: IDetailNftData) => void;
}

const defaultState = {
  isProcessing: false,
  transactions: [],
  processedTransactions: [],
  retryCallback: null,
  nftData: undefined,
  customTitle: undefined,
  warningDescription: undefined,
};

const useTransactionStore = create<ITransactionStore>((set, get) => ({
  ...defaultState,
  startProcessing: (transactions: ITransactionInfo[]) => {
    set({
      isProcessing: true,
      transactions: transactions,
    });
  },
  closeTransactionProcess: () => {
    toggleModalOpenClass(false);
    set({
      ...defaultState,
    });
  },
  updateCurrentTransaction: (
    transaction: ITransactionInfo,
    retryCallback?: () => Promise<void>
  ) => {
    const transactions = [...get().transactions];
    const txIndex = transactions.findIndex(
      (item) => item.number === transaction.number
    );
    transactions[txIndex] = {
      number: transactions[txIndex].number,
      transactionState: transaction.transactionState,
      txid: transaction.txid,
      description: transaction.description,
    };

    set({
      transactions: transactions,
      retryCallback: retryCallback ?? null,
    });
  },
  updateProcessedTransactions: (transaction: ITransactionInfo) => {
    const processedTx = get().processedTransactions;
    set({
      processedTransactions: [...processedTx, transaction],
    });
  },
  addTransactions: (transactions: ITransactionInfo[]) =>
    set(() => ({ transactions: [...get().transactions, ...transactions] })),
  setWarningDescription: (description: string) =>
    set(() => ({ warningDescription: description })),
  setCustomTitle: (title: string) => set(() => ({ customTitle: title })),
  setNftData: (data: IDetailNftData | undefined) =>
    set(() => ({ nftData: data })),
}));

export default useTransactionStore;
